import { useCallback, useMemo, useRef, useState } from "react";
import classNames from "./EventDetails.module.css";
import { Link } from "react-router-dom";
import { getUrl, getLanguageName } from "../../../types";
import { IoCheckmark, IoDownloadOutline, IoMailOutline } from "react-icons/io5";
import { AiOutlinePrinter } from "react-icons/ai";
import { BsCopy } from "react-icons/bs";
import { toDataURL } from "qrcode";
import Loader from "../../common/Loader";
import EventDate from "./EventDate";
import { useRoom, useRoomId } from "../../context/room/room";
import Button from "../../common/Button";

import speakerImg from "../../../assets/event/speaker.webp";
import attendeeImg from "../../../assets/event/attendee.webp";
import interpreterImg from "../../../assets/event/interpreter.webp";
import ShareDialog from "../../common/dialogs/ShareDialog";
import Breadcrumbs from "../../common/Breadcrumbs";

type QRCodeProps = {
  type: "speaker" | "interpreter" | "attendee";
  pin?: string;
  title: string;
  description: string;
};

function OneQRCode({ type, pin, title, description }: QRCodeProps) {
  const urlRoomId = useRoomId();
  const url = useMemo(() => {
    const roomId = urlRoomId;
    switch (type) {
      case "speaker":
        return getUrl(`/event/${roomId}/speaker?pin=${pin}`);
      case "interpreter":
        return getUrl(`/event/${roomId}/interpreter?pin=${pin}`);
      case "attendee":
        return getUrl(`/event/${roomId}`);
    }
  }, [type, pin, urlRoomId]);

  const [dataURL, setDataURL] = useState<string>();

  const qrCodeCanvas = useCallback(
    async (node: HTMLCanvasElement | null) => {
      if (node !== null) {
        setDataURL(await toDataURL(node, url, { width: 160 }));
      }
    },
    [url]
  );

  const download = useCallback(() => {
    if (!dataURL) return;
    const link = document.createElement("a");
    link.download = `${type}_qr_code.png`;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [type, dataURL]);

  const print = useCallback(() => {
    if (!dataURL) return;
    const newWindow = window.open("", "_blank", "width=600,height=600");
    if (!newWindow) return;

    const style = newWindow.document.createElement("style");
    style.textContent = `
      body, html {
        margin: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: black;
      }
      img { width: 100%; }
    `;
    newWindow.document.head.appendChild(style);
    const img = new Image();
    img.src = dataURL;
    newWindow.document.body.appendChild(img);
    newWindow.print();
  }, [dataURL]);

  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const copyURL = useCallback(() => {
    clearTimeout(copiedTimeout.current);
    navigator.clipboard.writeText(url);
    setCopied(true);
    copiedTimeout.current = setTimeout(() => {
      setCopied(false);
    }, 1500);
  }, [url]);

  const urlType = useMemo(() => {
    switch (type) {
      case "attendee":
        return "an attendee";
      case "interpreter":
        return "an interpreter";
      case "speaker":
        return "a speaker";
    }
  }, [type]);

  return (
    <>
      <div className={classNames.QRCodeDetails}>
        <div className={classNames.Text}>
          <h2>{title}</h2>
          <p>{description}</p>
          <div className={classNames.Link}>
            <Link target="_blank" rel="noreferrer" to={url}>
              {url}
            </Link>
            {!copied ? (
              <BsCopy onClick={copyURL} />
            ) : (
              <IoCheckmark onClick={copyURL} />
            )}
          </div>
        </div>
        <canvas ref={qrCodeCanvas} className={classNames.QRCode} />
      </div>
      <div className={classNames.ShareActions}>
        <IoDownloadOutline onClick={download} />
        <Link
          target="_blank"
          to={`mailto:?body=${encodeURIComponent(
            "Copy and paste this link to access real-time translation as " +
              urlType +
              ": " +
              url
          )}&subject=${encodeURIComponent(
            "Join the Langfinity event for live translation!"
          )}`}
        >
          <IoMailOutline />
        </Link>
        <AiOutlinePrinter onClick={print} />
      </div>
    </>
  );
}

function EventDetails() {
  const { room, organization, loading } = useRoom();

  if (!room || !organization || loading) {
    return <Loader />;
  }

  const sections = [
    {
      image: speakerImg,
      type: "speaker" as const,
      title: "I'm a speaker",
      description:
        "Share the URL or QR code to invite a speaker to join the event or conference",
    },
    {
      image: attendeeImg,
      type: "attendee" as const,
      title: "I'm an attendee",
      description:
        "Share the URL or QR code to invite an attendee to join the event or conference",
    },
    {
      image: interpreterImg,
      type: "interpreter" as const,
      title: "I'm an interpreter",
      description:
        "Share the URL or QR code to invite an interpreter to interpret the event or conference",
    },
  ];

  return (
    <div className={classNames.EventDetails}>
      <Breadcrumbs
        items={[
          { label: "Home", url: "/" },
          { label: "Meetings", url: "/meetings" },
          { label: room.name!, url: location.href },
        ]}
      />
      <div className={classNames.Header}>
        <div className={classNames.Title}>
          <div>
            <h1>{room.name}</h1>
            <div className={classNames.Badge}>Langfinity</div>
          </div>
          <div>
            {room.start_time && room.end_time && (
              <h2>
                <EventDate
                  start={room.start_time.toString()}
                  end={room.end_time.toString()}
                  reoccurring={room.reoccurring}
                />
              </h2>
            )}
            <h2>{room.languages.map((l) => getLanguageName(l)).join(", ")}</h2>
            {room.auto_record && <h2>Recording enabled</h2>}
          </div>
        </div>

        <div className={classNames.HeaderButtons}>
          <Link to={`/edit/${room.id}`}>
            <Button
              color="gray"
              backgroundColor="black"
              borderColor="dark-gray"
            >
              Edit
            </Button>
          </Link>
          <ShareDialog
            trigger={
              <Button
                color="gray"
                backgroundColor="black"
                borderColor="dark-gray"
              >
                Share
              </Button>
            }
            shareUrl={window.location.href}
            description="Copy the link to share this event with admins"
          />
        </div>
      </div>

      <h2 className={classNames.InstructionsTitle}>Instructions</h2>

      <div className={classNames.QRCodes}>
        {sections
          .filter(
            (section) =>
              section.type !== "interpreter" ||
              (section.type === "interpreter" &&
                organization.human_interpreter_enabled)
          )
          .map((section) => (
            <div key={section.type} className={classNames.Card}>
              <img
                src={section.image}
                className={classNames.InstructionImage}
              />

              <div className={classNames.InstructionSection}>
                <OneQRCode
                  title={section.title}
                  description={section.description}
                  type={section.type}
                  pin={room.pin}
                />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

export default EventDetails;
